/**
 * Standalone client for a `@davepi/agent` HTTP channel.
 *
 * The agent is a separate process from the davepi backend (it connects to
 * davepi over MCP), so this client is intentionally decoupled from the
 * davepi HTTP client / AuthProvider: it takes a base URL and talks to the
 * agent's own `GET /health` and `POST /chat` (SSE) endpoints. Chat runs as
 * the agent's configured identity (service mode) — the browser sends no
 * bearer — so nothing here reads the admin session.
 *
 * The agent must allow the admin's origin via `AGENT_CORS_ORIGINS`; requests
 * are sent with `credentials: 'include'` so per-user (cookie) mode keeps
 * working if the operator later switches the agent to it.
 */

/** Identity block returned by the agent's `GET /health`. */
export interface AgentInfo {
  ok: boolean;
  agent: string;
  auth: string;
  /** `AGENT_KEY` of the process, or `null` when unset. */
  agentKey: string | null;
  /** Human-friendly name (the key title-cased, or `'Assistant'`). */
  name: string;
  /** True when the agent fronts a team it delegates to. */
  isTeam: boolean;
  /** Roster of specialists the leader delegates to (empty for a solo agent). */
  team: Array<{ key: string; name: string }>;
}

/** A table payload emitted by the agent's `render_table` tool. */
export interface AgentTablePayload {
  type: 'table';
  title: string | null;
  columns: Array<{ key: string; label: string }>;
  rows: Array<Record<string, unknown>>;
}

/** A chart payload emitted by the agent's `render_chart` tool. */
export interface AgentChartPayload {
  type: 'chart';
  title: string | null;
  vegaLiteSpec: unknown;
}

export type AgentRenderPayload = AgentTablePayload | AgentChartPayload;

/**
 * Normalised chat event surfaced to the UI. Mirrors the orchestrator's SSE
 * events (`token` / `tool_call` / `tool_result` / `render` / `final`) plus
 * the HTTP channel's terminal `done` / `error`.
 */
export type AgentChatEvent =
  | { type: 'token'; text: string }
  | { type: 'tool_call'; name: string; args?: unknown }
  | { type: 'tool_result'; name: string; result?: unknown }
  | { type: 'render'; payload: AgentRenderPayload }
  | { type: 'final'; text: string; history?: ChatMessage[] }
  | { type: 'done' }
  | { type: 'error'; code: string; message: string; linkUrl?: string };

/** A single turn in the conversation, round-tripped to the agent as history. */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Lifecycle state of a persisted conversation. */
export type ConversationStatus = 'open' | 'resolved' | 'abandoned' | (string & {});

/**
 * A conversation as listed by `GET /conversations` — metadata only, no
 * transcript. Newest first, keyed by the stable `conversationId` the client
 * mints and sends on `POST /chat`.
 */
export interface ConversationSummary {
  conversationId: string;
  channelUserId: string | null;
  status: ConversationStatus;
  /** ISO timestamp of the most recent turn. */
  lastTurnAt: string;
  snapshotAt?: string | null;
  /** Short preview of the conversation (e.g. the opening prompt). */
  preview: string | null;
}

/** A full conversation transcript from `GET /conversations/:id`, ready to resume. */
export interface ConversationDetail {
  conversationId: string;
  status: ConversationStatus;
  lastTurnAt: string;
  /** Shaped exactly as `POST /chat`'s `history` — load straight into the UI. */
  history: ChatMessage[];
}

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}${path}`;
}

/**
 * Probe the agent's `GET /health`. Resolves to the parsed {@link AgentInfo}
 * when the agent is reachable and reports an agent, or `null` otherwise (no
 * agent configured, network error, non-2xx, or a non-agent response). Never
 * throws — a missing agent is a normal state, not an error.
 */
export async function probeAgent(
  baseUrl: string,
  signal?: AbortSignal
): Promise<AgentInfo | null> {
  try {
    const res = await fetch(joinUrl(baseUrl, '/health'), {
      method: 'GET',
      signal,
      credentials: 'include',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<AgentInfo>;
    if (!body || body.agent !== 'davepi-agent') return null;
    return {
      ok: body.ok ?? true,
      agent: body.agent,
      auth: body.auth ?? 'service',
      agentKey: body.agentKey ?? null,
      name: body.name || 'Assistant',
      isTeam: Boolean(body.isTeam),
      team: Array.isArray(body.team) ? body.team : [],
    };
  } catch {
    return null;
  }
}

/**
 * Stream a chat turn from the agent's `POST /chat` (Server-Sent Events).
 *
 * Each SSE frame is parsed and forwarded to `onEvent`. Resolves when the
 * stream ends (a `done` event or the connection closing); rejects only on a
 * transport-level failure before any event arrived. Errors the agent reports
 * mid-stream arrive as an `{ type: 'error' }` event rather than a rejection,
 * matching the HTTP channel's error shaping.
 *
 * Pass an `AbortSignal` to cancel an in-flight turn (e.g. component unmount).
 */
export async function streamAgentChat(
  baseUrl: string,
  params: {
    message: string;
    history?: ChatMessage[];
    /**
     * Stable id for this conversation. Reuse across turns to append to one
     * persisted transcript (service mode); omit → nothing persisted. In
     * per-user mode the agent keys on its own cookie and ignores this.
     */
    conversationId?: string;
    signal?: AbortSignal;
  },
  onEvent: (event: AgentChatEvent) => void
): Promise<void> {
  const res = await fetch(joinUrl(baseUrl, '/chat'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    signal: params.signal,
    body: JSON.stringify({
      message: params.message,
      history: params.history ?? [],
      stream: true,
      // Only serialised when set — omitting it preserves the no-persist default.
      ...(params.conversationId ? { conversationId: params.conversationId } : {}),
    }),
  });

  // Non-streaming error (e.g. 400/401 before headers flush): the body is a
  // JSON error envelope, not an event stream.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('text/event-stream')) {
    let code = 'INTERNAL_ERROR';
    let message = `Agent responded ${res.status}`;
    let linkUrl: string | undefined;
    try {
      const body = await res.json();
      if (body?.error) {
        code = body.error.code ?? code;
        message = body.error.message ?? message;
        linkUrl = body.error.linkUrl;
      }
    } catch {
      /* keep defaults */
    }
    onEvent({ type: 'error', code, message, linkUrl });
    return;
  }

  if (!res.body) {
    onEvent({ type: 'error', code: 'NO_BODY', message: 'Agent returned an empty stream' });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawTerminal = false;

  const flushFrame = (frame: string) => {
    // An SSE frame is a set of `field: value` lines. We only care about the
    // `data:` line — the event type is redundantly carried inside the JSON
    // payload (the HTTP channel writes `event: <type>` + `data: <full evt>`).
    const dataLines = frame
      .split('\n')
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5).trimStart());
    if (!dataLines.length) return;
    const raw = dataLines.join('\n');
    let evt: Record<string, unknown>;
    try {
      evt = JSON.parse(raw);
    } catch {
      return;
    }
    const type = evt.type as AgentChatEvent['type'] | undefined;
    if (!type) return;
    if (type === 'done' || type === 'final' || type === 'error') sawTerminal = true;
    onEvent(evt as unknown as AgentChatEvent);
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    // Normalise CRLF → LF so frames delimited with `\r\n\r\n` (a valid SSE
    // line ending) frame correctly under the `\n\n` split below, and so
    // `data:` lines don't keep a trailing `\r` that would corrupt the JSON.
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    // Frames are separated by a blank line (\n\n).
    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      if (frame.trim()) flushFrame(frame);
    }
  }
  if (buffer.trim()) flushFrame(buffer);
  // The server should close with a `done` (or `error` / `final`) event, but a
  // dropped connection can hit EOF without one. Synthesise a terminal `done`
  // so the caller never leaves the turn stuck in a pending state.
  if (!sawTerminal) onEvent({ type: 'done' });
}

/**
 * Outcome of {@link listConversations}. `conversations` is the listed rows on
 * success, else `null`. `unsupported` is `true` only when the route is
 * *definitively* absent (HTTP 404) — an agent older than 0.5.0 — so callers can
 * hide the sidebar for good. Any other failure (network error, 5xx, malformed
 * body) leaves `unsupported: false`, marking it a transient miss worth
 * retrying rather than a permanent capability gap.
 */
export interface ConversationListResult {
  conversations: ConversationSummary[] | null;
  unsupported: boolean;
}

/** Coerce an unknown array into well-formed {@link ConversationSummary} rows. */
function sanitizeSummaries(items: unknown[]): ConversationSummary[] {
  const out: ConversationSummary[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const c = item as Record<string, unknown>;
    if (typeof c.conversationId !== 'string') continue;
    out.push({
      conversationId: c.conversationId,
      channelUserId: typeof c.channelUserId === 'string' ? c.channelUserId : null,
      status: typeof c.status === 'string' ? (c.status as ConversationStatus) : 'open',
      lastTurnAt: typeof c.lastTurnAt === 'string' ? c.lastTurnAt : '',
      snapshotAt: typeof c.snapshotAt === 'string' ? c.snapshotAt : null,
      preview: typeof c.preview === 'string' ? c.preview : null,
    });
  }
  return out;
}

/** Keep only well-formed `{ role, content }` turns from an unknown history. */
function sanitizeHistory(history: unknown): ChatMessage[] {
  if (!Array.isArray(history)) return [];
  const out: ChatMessage[] = [];
  for (const item of history) {
    if (!item || typeof item !== 'object') continue;
    const m = item as Record<string, unknown>;
    if ((m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string') {
      out.push({ role: m.role, content: m.content });
    }
  }
  return out;
}

/**
 * List persisted conversations from the agent's `GET /conversations`, newest
 * first. Never throws — returns a {@link ConversationListResult} that
 * distinguishes a definitively-absent route (404, `unsupported: true`) from a
 * transient miss (`conversations: null, unsupported: false`), so the sidebar
 * can hide permanently for old agents yet recover from a passing blip. Rows are
 * sanitised, so callers can render them without extra guards.
 */
export async function listConversations(
  baseUrl: string,
  signal?: AbortSignal
): Promise<ConversationListResult> {
  try {
    const res = await fetch(joinUrl(baseUrl, '/conversations'), {
      method: 'GET',
      signal,
      credentials: 'include',
    });
    if (res.status === 404) return { conversations: null, unsupported: true };
    if (!res.ok) return { conversations: null, unsupported: false };
    const body = (await res.json()) as { conversations?: unknown };
    if (!body || !Array.isArray(body.conversations)) {
      return { conversations: null, unsupported: false };
    }
    return { conversations: sanitizeSummaries(body.conversations), unsupported: false };
  } catch {
    return { conversations: null, unsupported: false };
  }
}

/**
 * Fetch a single conversation's full transcript from
 * `GET /conversations/:conversationId` to resume it. The returned `history`
 * is shaped exactly as `POST /chat`'s `history`, so it loads straight into the
 * chat transcript. Throws on a non-2xx (e.g. 404 when the id is unknown) so a
 * failed click-to-resume can surface an error to the user.
 */
export async function getConversation(
  baseUrl: string,
  conversationId: string,
  signal?: AbortSignal
): Promise<ConversationDetail> {
  const res = await fetch(
    joinUrl(baseUrl, `/conversations/${encodeURIComponent(conversationId)}`),
    { method: 'GET', signal, credentials: 'include' }
  );
  if (!res.ok) {
    throw new Error(
      res.status === 404
        ? 'Conversation not found'
        : `Agent responded ${res.status}`
    );
  }
  const body = (await res.json()) as Partial<ConversationDetail> & { history?: unknown };
  return {
    conversationId:
      typeof body.conversationId === 'string' ? body.conversationId : conversationId,
    status: typeof body.status === 'string' ? body.status : 'open',
    lastTurnAt: typeof body.lastTurnAt === 'string' ? body.lastTurnAt : '',
    history: sanitizeHistory(body.history),
  };
}
